import {
  Radio,
  RadioGroup,
  FormControlLabel,
  OutlinedInput,
  InputAdornment,
  Popover,
  styled,
  FormControl,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useState, useEffect } from 'react';

import { useSearchParams } from 'react-router';

import { ExpandLess, ExpandMore } from '@mui/icons-material';

import { formatFilterDateRange } from '@app-velocity/common';

import { I18nResources, useI18n } from '@app-velocity/i18n';

import { Box, ButtonsContainer, Typography, Button } from '../..';

import { getEpochRange } from './helpers/date-range';

const StyledPopover = styled(Popover)(({ theme }) => ({
  '.MuiPaper-root': {
    display: 'flex',
    padding: theme.spacing(3),
    flexDirection: 'column',
    gap: theme.spacing(4),
    width: theme.spacing(60),
  },
}));

const StyledLabel = styled(Typography)({
  fontSize: 12,
  fontWeight: 500,
});

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const StyledInput = styled(OutlinedInput)({
  '&.MuiInputBase-root': {
    cursor: 'pointer',
  },
  input: {
    cursor: 'pointer',
  },
});

const StyledFilterLabel = styled(Typography)(({ theme }) => ({
  color: 'var(--text-text-secondary-color)',
  fontSize: 12,
  lineHeight: 1.5,
  marginBottom: theme.spacing(1),
}));

const StyledFormControl = styled(FormControl)({
  width: '100%',
});

type DateOption = 'all' | '12m' | '6m' | '30d' | '7d' | '24h' | 'custom';

const OPTIONS: { value: DateOption; label: I18nResources }[] = [
  { value: 'all', label: 'shared:date_filter.all' },
  { value: '12m', label: 'shared:date_filter.last_12_months' },
  { value: '6m', label: 'shared:date_filter.last_6_months' },
  { value: '30d', label: 'shared:date_filter.last_30_days' },
  { value: '7d', label: 'shared:date_filter.last_week' },
  { value: '24h', label: 'shared:date_filter.last_24_hours' },
  { value: 'custom', label: 'shared:date_filter.custom' },
];

export const DateFilter = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const close = () => setAnchorEl(null);
  const { t } = useI18n();
  const [value, setValue] = useState<DateOption>('all');
  const [tempValue, setTempValue] = useState<DateOption>('all');
  const [searchParams, setSearchParams] = useSearchParams();

  const dateParam = searchParams.get('date');
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');

  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);

  const [tempFrom, setTempFrom] = useState<Date | null>(null);
  const [tempTo, setTempTo] = useState<Date | null>(null);

  const reset = () => {
    setTempValue('all');
    setTempFrom(null);
    setTempTo(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('from');
      next.delete('to');
      next.delete('date');

      return next;
    });
    close();
  };

  const apply = () => {
    const { from, to } = getEpochRange(tempValue, tempFrom, tempTo);

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      if (from) next.set('from', String(from));
      else next.delete('from');

      if (to) next.set('to', String(to));
      else next.delete('to');

      next.set('date', tempValue); // optional

      return next;
    });

    setValue(tempValue);
    setFrom(tempFrom);
    setTo(tempTo);
    close();
  };

  useEffect(() => {
    if (!dateParam) {
      reset();

      return;
    }

    setValue(dateParam as DateOption);
    setTempValue(dateParam as DateOption);

    if (dateParam === 'custom' && fromParam && toParam) {
      const fromDate = new Date(Number(fromParam));
      const toDate = new Date(Number(toParam));

      if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
        setFrom(fromDate);
        setTo(toDate);
        setTempFrom(fromDate);
        setTempTo(toDate);
      }
    }
  }, [dateParam, fromParam, toParam]);

  const getLabel = () => {
    if (value !== 'custom') {
      const label = OPTIONS.find((o) => o.value === value)?.label;

      return label ? t(label) : '-';
    }

    if (from && to) {
      return formatFilterDateRange(from, to);
    }

    return t('shared:date_filter.custom');
  };

  return (
    <>
      <StyledFormControl>
        <StyledFilterLabel>{t('shared:date_filter.title')}</StyledFilterLabel>
        <StyledInput
          role="button"
          readOnly
          label="Test"
          value={getLabel()}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          endAdornment={
            <InputAdornment position="end">
              {open ? <ExpandLess /> : <ExpandMore />}
            </InputAdornment>
          }
        />
      </StyledFormControl>

      <StyledPopover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <>
          <StyledRadioGroup
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value as DateOption)}
          >
            {OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                label={t(option.label)}
                control={<Radio />}
              />
            ))}
          </StyledRadioGroup>

          {tempValue === 'custom' && (
            <Box>
              <StyledLabel>{t('shared:from')}</StyledLabel>

              <DatePicker
                value={tempFrom}
                onChange={(v) => setTempFrom(v)}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    placeholder: 'dd.mm.yyyy',
                  },
                }}
              />

              <StyledLabel>{t('shared:to')}</StyledLabel>

              <DatePicker
                value={tempTo}
                onChange={(v) => setTempTo(v)}
                minDate={tempFrom ?? undefined}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    placeholder: 'dd.mm.yyyy',
                  },
                }}
              />
            </Box>
          )}

          <ButtonsContainer>
            <Button
              onClick={reset}
              variant="contained"
              color="secondary"
              size="small"
            >
              {t('shared:reset')}
            </Button>

            <Button
              variant="contained"
              disabled={tempValue === 'custom' && (!tempFrom || !tempTo)}
              onClick={apply}
              size="small"
            >
              {t('shared:apply')}
            </Button>
          </ButtonsContainer>
        </>
      </StyledPopover>
    </>
  );
};
